import type { AgentAbout, CopyResult } from '@ralph/contracts'
import { AlertTriangle, ArrowLeft, ArrowRight, Copy, Flag, Info, SlidersHorizontal } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { accentTileStyle } from '@/components/marketplace/accents.ts'
import { detailsAction, detailsHref } from '@/components/marketplace/agent-sheet.ts'
import { OfficialMark } from '@/components/marketplace/consumer/AgentBadges.tsx'
import { AudienceLabel } from '@/components/marketplace/consumer/AudienceLabel.tsx'
import { monogram } from '@/components/marketplace/consumer/agent-identity.ts'
import { DisclosurePanel } from '@/components/marketplace/consumer/DisclosurePanel.tsx'
import { FirstUseNoticeDialog } from '@/components/marketplace/consumer/FirstUseNoticeDialog.tsx'
import { OwnerLabel } from '@/components/marketplace/consumer/OwnerLabel.tsx'
import { ReportDialog } from '@/components/marketplace/consumer/ReportDialog.tsx'
import { Button, ButtonChip, EmptyState, Panel, Spinner } from '@/components/ui/primitives.tsx'
import { useCreateConversation } from '@/hooks/use-api.ts'
import { useAcknowledgeNotice, useAgentAbout, useCopyAgent } from '@/hooks/use-marketplace.ts'
import { useI18n, useT } from '@/i18n/index.ts'

/**
 * The listing page — `/agents/:agentId` (AGM-003).
 *
 * The whole point of this screen is that a person can decide whether to trust an agent
 * *without running it*. So the order is: who made it and for whom, what state it is in, what
 * it does, and only then the machine-generated disclosure panel, which is the part that
 * cannot be written by the maker.
 *
 * Starters never auto-send (AGM-005 abuse case): a deceptive starter that fired on one click
 * would be an action taken with the consumer's authority before they read a word of it. They
 * open a chat with the composer prefilled, which is one keystroke away from sending and one
 * glance away from not.
 *
 * The redesign gives the listing a masthead — an identity tile in the agent's own colour, the
 * name in the display serif, and the actions immediately beside them — because this page is
 * where an agent is introduced, and everything below it is evidence for or against that
 * introduction. Reporting moved up into that row from the foot of the page: it is one of the
 * three things a reader can do here, and burying it made the one action that costs the reader
 * nothing the hardest one to find (AGM-123). What it does is unchanged — the same dialog, the
 * same dedupe, still the only write this page offers a consumer.
 */
export const AgentAboutPage = () => {
  const { agentId } = useParams<{ agentId: string }>()
  const about = useAgentAbout(agentId)
  const navigate = useNavigate()
  // Bound in the body rather than captured at module scope, so the page — and the date beside
  // "Updated" — follows a language switch instead of freezing in whichever locale imported
  // this file (PRD-27 F-3, LOC-012).
  const { t: messages, formatDate } = useI18n()
  const t = messages.marketplace.gallery.about
  const report = messages.marketplace.report

  const createConversation = useCreateConversation()
  const acknowledge = useAcknowledgeNotice()
  const copyAgent = useCopyAgent()

  // The action waiting behind the first-use notice. Holding the prefill here is what lets
  // the notice interrupt a starter and then continue it, rather than dropping the user back
  // on the page with no idea what happened to their click.
  const [pendingPrefill, setPendingPrefill] = useState<string | null>(null)
  const [noticeOpen, setNoticeOpen] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const [startFailed, setStartFailed] = useState(false)
  const [copyResult, setCopyResult] = useState<CopyResult | null>(null)
  const [reportOpen, setReportOpen] = useState(false)

  if (about.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="flex items-center gap-2 text-[13px] text-fg-subtle">
          <Spinner />
          {t.loading}
        </p>
      </div>
    )
  }

  // A listing the caller may not see returns 404, and so does one that never existed —
  // AGM-002 requires the two to be indistinguishable, which includes here.
  //
  // Gated on the ABSENCE of data, not on `isError`. A background refetch that fails leaves
  // `status: 'error'` with the previous data intact (query-core keeps it and flags it stale),
  // and `useAgentAbout` does not retry — so keying on `isError` replaced a page the reader was
  // already looking at with "it may not be shared with you" the first time a request blipped.
  // That is the same mistake `SheetScreen` documents and avoids two files away (DTL-014):
  // answering an outage with a permission story sends somebody hunting for access they hold.
  if (!about.data) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState
          title={t.notFound.title}
          body={t.notFound.body}
          action={
            <Link to="/agents">
              <Button size="sm">{t.back}</Button>
            </Link>
          }
        />
      </div>
    )
  }

  const listing = about.data
  const noticesDue = acknowledged ? [] : listing.noticesDue
  const canChat = listing.state === 'published'

  const startChat = async (prefill: string) => {
    setStartFailed(false)
    try {
      const created = await createConversation.mutateAsync({ agentId: listing.agentId })
      navigate(`/c/${created.id}`, prefill ? { state: { prefill } } : undefined)
    } catch {
      setStartFailed(true)
    }
  }

  const beginChat = (prefill: string) => {
    if (noticesDue.length > 0) {
      setPendingPrefill(prefill)
      setNoticeOpen(true)
      return
    }
    void startChat(prefill)
  }

  const confirmNotice = async () => {
    try {
      // Each capability class is acknowledged separately: the server records them per class
      // so that gaining actions later re-fires only that notice (AGM-010).
      for (const capability of listing.noticesDue) {
        await acknowledge.mutateAsync({ agentId: listing.agentId, capability })
      }
      setAcknowledged(true)
      setNoticeOpen(false)
      const prefill = pendingPrefill ?? ''
      setPendingPrefill(null)
      await startChat(prefill)
    } catch {
      // The dialog stays open and shows its own failure line; falling through to the chat
      // would leave the acknowledgement unrecorded and the notice due again tomorrow.
    }
  }

  const runCopy = async () => {
    setCopyResult(null)
    try {
      const result = await copyAgent.mutateAsync(listing.agentId)
      // Nothing was dropped, so there is nothing to read: go straight to the copy.
      if (result.dropped.length === 0) navigate(`/agents/${result.agentId}/edit`)
      else setCopyResult(result)
    } catch {
      // The failure line below renders off the mutation's own error state; letting the
      // rejection escape would leave an unhandled promise and no message on screen.
    }
  }

  return (
    <div className="fade-in min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[952px] px-5 pb-9 pt-1 sm:px-8 lg:px-10">
        <Link
          to="/agents"
          className="readout mt-3.5 mb-5 inline-flex h-[29px] items-center gap-[7px] rounded-full border border-rule bg-card pr-3 pl-[9px] text-[10px] tracking-[0.05em] text-fg-muted transition-[color,border-color,transform] duration-200 hover:-translate-y-px hover:border-rule-strong hover:text-fg"
        >
          <ArrowLeft size={12} strokeWidth={2.2} aria-hidden />
          {t.back}
        </Link>

        <header className="flex items-start gap-4 sm:gap-5">
          {/*
            The agent's own tile, not the assistant's orb: an agent is a configured thing a
            person made, and the orb marks the model itself. Built here rather than through
            `AgentAvatar` because this is the one 66px instance in the product — the fill and
            the depth still come from `accentTileStyle`, and the radius keeps the family's
            rule of roughly a third of the size, so it reads as the same object made larger.
          */}
          <span
            aria-hidden
            style={accentTileStyle(listing.accent)}
            className="flex h-[54px] w-[54px] flex-none items-center justify-center rounded-[18px] font-mono text-[22px] font-semibold tracking-[0.02em] select-none sm:h-[66px] sm:w-[66px] sm:rounded-[22px] sm:text-[26px]"
          >
            {listing.icon || monogram(listing.displayName)}
          </span>

          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
              <h1 className="display text-[30px] sm:text-[36px]">{listing.displayName}</h1>
              {listing.official && <OfficialMark />}
            </div>

            {listing.tagline && (
              <p className="mt-2 max-w-[524px] text-pretty text-[14.5px] leading-[1.6] text-fg-muted">
                {listing.tagline}
              </p>
            )}

            {/*
              Who owns it and who can use it are drawn, not spelled (PRD-26 GRP-102): a group is
              its avatar and its name, linked to its page. Sans rather than the readout's mono —
              these are names of people and groups, not measurements.
            */}
            <dl className="mt-3.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
              <Meta label={t.ownerLabel}>
                <OwnerLabel owner={listing.owner} size={16} to />
              </Meta>
              <Meta label={t.audienceLabel}>
                <AudienceLabel audience={listing.audience} owner={listing.owner} />
              </Meta>
              <Meta label={t.updatedLabel}>
                <span className="readout">{formatDate(listing.updatedAt)}</span>
              </Meta>
            </dl>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                variant="primary"
                size="lg"
                trailing={
                  <ButtonChip size={25}>
                    <ArrowRight size={13} strokeWidth={2.4} />
                  </ButtonChip>
                }
                onClick={() => beginChat('')}
                disabled={!canChat || createConversation.isPending}
              >
                {createConversation.isPending ? t.starting : t.startChat}
              </Button>

              {/*
                The way into the detail sheet (PRD-22 DTL-001), and the second thing a reader
                wants from this page after starting a chat: how is this thing put together, and
                may I change it. One destination, one icon, and a label that commits — "Edit
                agent" only where the sheet will actually let them, so the control is never
                named for something it will not do.
              */}
              <Link to={detailsHref(listing.agentId)}>
                <Button size="lg" icon={<SlidersHorizontal size={13} aria-hidden />}>
                  {detailsAction(listing.viewerRole) === 'edit' ? t.editAgent : t.viewDetails}
                </Button>
              </Link>

              {listing.copyable && (
                <Button
                  size="lg"
                  icon={<Copy size={13} aria-hidden />}
                  onClick={() => void runCopy()}
                  disabled={copyAgent.isPending}
                >
                  {copyAgent.isPending ? t.copying : t.saveCopy}
                </Button>
              )}

              <Button size="lg" icon={<Flag size={13} aria-hidden />} onClick={() => setReportOpen(true)}>
                {report.open}
              </Button>
            </div>

            {canChat && noticesDue.length > 0 && (
              <p className="mt-2.5 flex items-center gap-1.5 text-[12.5px] text-fg-subtle">
                <Info size={12} aria-hidden />
                {messages.marketplace.gallery.notice.beforeFirstChat}
              </p>
            )}

            {startFailed && (
              <p role="alert" className="mt-2.5 text-[13px] text-alert">
                {t.startFailed}
              </p>
            )}
            {copyAgent.isError && (
              <p role="alert" className="mt-2.5 text-[13px] text-alert">
                {t.copyFailed}
              </p>
            )}
          </div>
        </header>

        <LifecycleBanner listing={listing} />

        {listing.description && (
          <p className="mt-5 whitespace-pre-wrap text-pretty text-[14.5px] leading-[1.6]">{listing.description}</p>
        )}

        {copyResult && <CopyReport result={copyResult} />}

        {listing.starters.length > 0 && canChat && (
          <section className="mt-7">
            <h2 className="eyebrow mb-1">{t.startersTitle}</h2>
            <p className="mb-2.5 text-[12.5px] text-fg-subtle">{t.starterHint}</p>
            <ul className="grid gap-2 sm:grid-cols-2">
              {listing.starters.map((starter, index) => (
                // The entrance sits on the row and the hover on the button, deliberately: a
                // finished `rise` keeps its filled transform, and an animation beats a normal
                // declaration, so sharing one element would leave the hover lift dead.
                // Capped at eight so a long list finishes arriving inside half a second
                // instead of dribbling in for the length of the page.
                <li key={starter} className="rise" style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}>
                  <button
                    type="button"
                    onClick={() => beginChat(starter)}
                    disabled={createConversation.isPending}
                    className="card card-hover flex w-full items-start gap-2 rounded-card px-3.5 py-2.5 text-left text-[13px] leading-snug text-fg-muted hover:text-fg disabled:pointer-events-none disabled:opacity-45"
                  >
                    <ArrowRight size={12} strokeWidth={2.2} className="mt-[3px] flex-none text-fg-subtle" aria-hidden />
                    <span className="min-w-0 flex-1 text-pretty">{starter}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/*
          The disclosure panel is the reader's checklist, and it comes after everything the
          listing says about itself so a suspicious claim above can be checked against it.
          DECISION: the comp's "How it has behaved" stats panel is not built. Runs, grounded
          rate, median latency and flag counts exist only on the owner-gated analytics route
          (`OwnerAnalytics`, and its `abstentionRate` is hardcoded null); `AgentAbout` carries
          no counts at all. A consumer reading this page has no such data, and inventing it
          on the one screen whose entire purpose is honest disclosure is the worst possible
          place to guess.
        */}
        <div className="mt-7">
          <DisclosurePanel disclosure={listing.disclosure} agentId={listing.agentId} agentName={listing.displayName} />
        </div>

        {listing.whatsNew && (
          <section className="mt-7">
            <h2 className="eyebrow mb-1.5">{t.whatsNewTitle}</h2>
            <p className="whitespace-pre-wrap text-pretty text-[13.5px] leading-relaxed text-fg-muted">
              {listing.whatsNew}
            </p>
          </section>
        )}
      </div>

      {noticeOpen && (
        <FirstUseNoticeDialog
          agentName={listing.displayName}
          capabilities={[...listing.noticesDue]}
          busy={acknowledge.isPending || createConversation.isPending}
          failed={acknowledge.isError}
          onAcknowledge={() => void confirmNotice()}
          onDismiss={() => {
            setNoticeOpen(false)
            setPendingPrefill(null)
          }}
        />
      )}

      {reportOpen && (
        <ReportDialog agentId={listing.agentId} agentName={listing.displayName} onClose={() => setReportOpen(false)} />
      )}
    </div>
  )
}

/**
 * The suspended / retired / draft banner (AGM-011).
 *
 * Consumer-safe wording only: `suspendedReason` is the public half of the reason an
 * administrator recorded, and the full one never leaves the owner's view.
 */
const LifecycleBanner = ({ listing }: { listing: AgentAbout }) => {
  // Its own read. `suspendedReason` beside it is an administrator's own words about one
  // agent — data, never translated (PRD-27 D-17).
  const t = useT().marketplace.gallery.about

  if (listing.state === 'published') return null

  const copy =
    listing.state === 'suspended'
      ? { title: t.suspendedTitle, body: listing.suspendedReason || t.suspendedBody }
      : listing.state === 'archived'
        ? { title: t.archivedTitle, body: t.archivedBody }
        : { title: t.draftTitle, body: t.draftBody }

  return (
    <div className="mt-5 rounded-card border border-alert/40 bg-alert-soft px-4 py-3 shadow-glass">
      <p className="flex items-start gap-2 text-[13.5px] leading-relaxed">
        <AlertTriangle size={14} className="mt-[3px] shrink-0 text-alert" aria-hidden />
        <span>
          <span className="font-semibold">{copy.title}</span> {copy.body}
          {listing.replacementAgentId && (
            <>
              {' '}
              <Link to={`/agents/${listing.replacementAgentId}`} className="underline underline-offset-2">
                {t.replacementLink}
              </Link>
            </>
          )}
        </span>
      </p>
    </div>
  )
}

/**
 * What a copy could not bring with it (AGM-028).
 *
 * Shown instead of navigating away, because the dropped list is the only place the copier
 * learns that their new agent is missing something its original had — a silent copy that
 * answers worse than the thing it was copied from is the failure this report prevents.
 */
const CopyReport = ({ result }: { result: CopyResult }) => {
  const t = useT().marketplace.gallery.about

  return (
    <Panel className="mt-5 px-4 py-3.5">
      <h2 className="font-sans text-[14px] font-semibold tracking-[-0.018em]">{t.copyDoneTitle}</h2>
      <p className="mt-1 text-[13px] leading-relaxed text-fg-muted">{t.copyDoneBody}</p>

      <p className="mt-3 flex items-start gap-1.5 text-[13px] font-semibold">
        <AlertTriangle size={13} className="mt-[3px] shrink-0 text-alert" aria-hidden />
        {t.copyDroppedTitle}
      </p>
      <p className="mt-1 text-[13px] leading-relaxed text-fg-muted">{t.copyDroppedBody}</p>
      <ul className="mt-1.5 space-y-1">
        {result.dropped.map((item) => (
          <li key={`${item.kind}-${item.name}`} className="text-[13px] leading-snug text-fg-muted">
            {item.kind === 'knowledge' ? t.copyDroppedKnowledge(item.name) : t.copyDroppedCapability(item.name)}
          </li>
        ))}
      </ul>

      <div className="mt-3.5">
        <Link to={`/agents/${result.agentId}/edit`}>
          <Button size="sm" variant="primary">
            {t.openCopy}
          </Button>
        </Link>
      </div>
    </Panel>
  )
}

/** A labelled fact in the masthead, in the same register as `Readout` but holding names. */
const Meta = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="inline-flex min-w-0 items-center gap-1.5">
    <dt className="eyebrow shrink-0">{label}</dt>
    <dd className="min-w-0 text-[12.5px] font-medium">{children}</dd>
  </div>
)
